import { useState } from 'react'
import useApiQuery from '../../hooks/useApiQuery'
import { fetchHubConnectors, installConnector } from '../../lib/api'
import { PageHeader, Card, Button, SearchBar, StatusBadge } from '../../components/ui'
import { useToast } from '../../contexts/ToastContext'

const CATEGORIES = ['All', 'CRM', 'Communication', 'E-commerce', 'Analytics', 'Developer', 'Storage', 'AI']

const FEATURED = [
  { slug: 'hubspot',   name: 'HubSpot',   cat: 'CRM',           icon: '🟠', desc: 'Sync contacts, deals and pipelines with HubSpot CRM.' },
  { slug: 'docusign',  name: 'DocuSign',  cat: 'Communication',  icon: '✍️', desc: 'Send and track e-signature requests automatically.' },
  { slug: 'stripe',    name: 'Stripe',    cat: 'E-commerce',     icon: '💳', desc: 'Payment events trigger workflows and campaigns.' },
  { slug: 'slack',     name: 'Slack',     cat: 'Communication',  icon: '💬', desc: 'Post alerts and notifications to Slack channels.' },
  { slug: 'shopify',   name: 'Shopify',   cat: 'E-commerce',     icon: '🛒', desc: 'Sync orders, customers and abandoned carts.' },
  { slug: 'github',    name: 'GitHub',    cat: 'Developer',      icon: '🐙', desc: 'Trigger automations from push events and PRs.' },
  { slug: 'gitlab',    name: 'GitLab',    cat: 'Developer',      icon: '🦊', desc: 'Connect repositories for CI/CD automation.' },
  { slug: 'zapier',    name: 'Zapier',    cat: 'Developer',      icon: '⚡', desc: '5000+ app connections via Zapier REST hooks.' },
  { slug: 'make',      name: 'Make',      cat: 'Developer',      icon: '🔀', desc: 'Visual automation with Make (formerly Integromat).' },
  { slug: 'mailchimp', name: 'Mailchimp', cat: 'Communication',  icon: '📧', desc: 'Sync subscriber lists and campaign analytics.' },
  { slug: 'google-analytics', name: 'Google Analytics', cat: 'Analytics', icon: '📊', desc: 'Pull GA4 metrics into your Systeza reports.' },
  { slug: 'aws-s3',    name: 'AWS S3',    cat: 'Storage',        icon: '☁️', desc: 'Store and retrieve files from S3 buckets.' },
  { slug: 'openai',    name: 'OpenAI',    cat: 'AI',             icon: '🤖', desc: 'Use GPT-4 in your content and workflow steps.' },
  { slug: 'anthropic', name: 'Anthropic', cat: 'AI',             icon: '🧠', desc: 'Claude AI for smart document generation.' },
]

export default function HubMarketplace() {
  const { success, error } = useToast()
  const [search, setSearch]     = useState('')
  const [category, setCategory] = useState('All')
  const [installing, setInstalling] = useState(null)
  const { data: installed = [] } = useApiQuery(() => fetchHubConnectors())

  const installedSlugs = new Set((installed || []).map(c => c.slug))

  const filtered = FEATURED.filter(c =>
    (category === 'All' || c.cat === category) &&
    (c.name.toLowerCase().includes(search.toLowerCase()) || c.desc.toLowerCase().includes(search.toLowerCase()))
  )

  const handleInstall = async (slug) => {
    setInstalling(slug)
    try {
      await installConnector(slug)
      success(`${slug} installed successfully!`)
    } catch { error('Installation failed. Please try again.') }
    finally { setInstalling(null) }
  }

  return (
    <div>
      <PageHeader
        title="Integration Hub"
        subtitle="Connect Systeza to 100+ tools and services"
        badge={{ label: 'NEW', className: 'badge badge-purple' }}
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <SearchBar value={search} onChange={setSearch} placeholder="Search integrations..." className="flex-1" />
        <div className="flex gap-2 overflow-x-auto pb-1">
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setCategory(cat)}
              className={`px-3 py-1.5 rounded-lg text-xs font-mono font-semibold whitespace-nowrap transition-all border ${
                category === cat
                  ? 'border-[#A855F7] text-[#A855F7]' : 'border-[var(--border-subtle)] text-[var(--text-dim)] hover:text-[var(--text-muted)]'
              }`}>
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map(connector => {
          const isInstalled = installedSlugs.has(connector.slug)
          return (
            <Card key={connector.slug} className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                  style={{ background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.2)' }}>
                  {connector.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white">{connector.name}</p>
                  <span className="badge badge-gray text-[9px]">{connector.cat}</span>
                </div>
                {isInstalled && <span className="badge badge-green">Installed</span>}
              </div>
              <p className="text-xs text-[var(--text-dim)] flex-1 leading-relaxed">{connector.desc}</p>
              {isInstalled ? (
                <button className="btn-ghost text-xs py-2 w-full justify-center border border-[var(--border-subtle)] rounded-lg">
                  Configure
                </button>
              ) : (
                <Button variant="secondary" className="w-full justify-center py-2 text-xs"
                  loading={installing === connector.slug}
                  onClick={() => handleInstall(connector.slug)}>
                  + Install
                </Button>
              )}
            </Card>
          )
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-20 text-[var(--text-dim)]">
          <div className="text-4xl mb-3">🔌</div>
          <p className="font-semibold text-white mb-1">No integrations found</p>
          <p className="text-sm">Try a different search or category</p>
        </div>
      )}
    </div>
  )
}
